/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * myCertificates
 * --------------
 * The participant's own certificates ("My certificates", /feed/certificates,
 * and the email link /certificate/<id>), from ONE read: the owner-scoped
 * public view of user_certificate_information (arcee command
 * configure_certificate_public_page). The view is owned through the pass
 * (owner_via: pass_id), so it holds the certificates of every pass this
 * person booked — their own and their family members' — and nobody else's.
 * Each record arrives with what it prints joined on: pass (user_info), event,
 * activity (+ activity_type, route + unit, venue) and choice (+ template).
 *
 * Everything here is plain functions; the page renders what they return.
 */

import { fetchPublicTableList } from '../../hooks/usePublicTableView';
import { parseStoredUtc, utcToWallClock } from '../../utils/zonedDateTime';
import { certificateValues } from './certificateFiller';

export const CERTIFICATE_VIEW_SLUG = 'user_certificate_information';
const PAGE_SIZE = 100;
const MAX_PAGES = 10;

const SPORT_ICONS = {
  RUN: 'directions_run',
  WALK: 'directions_walk',
  HIKE: 'hiking',
  CYCLING: 'directions_bike',
  RIDE: 'directions_bike',
  SWIM: 'pool',
  YOGA: 'self_improvement',
  DRIVE: 'directions_car',
};

/** A sport's icon (Material Symbols) from its activity_type code. */
export function sportIcon(code) {
  return SPORT_ICONS[String(code || '').toUpperCase()] || 'workspace_premium';
}

function asObject(raw) {
  if (raw && typeof raw === 'object') return raw;
  try { return raw ? JSON.parse(raw) : {}; } catch (_) { return {}; }
}

const text = (v) => String(v ?? '').trim();

/** "22 Oct 2026" for a stored UTC timestamp, in the viewer's own zone. */
export function issuedLabel(value) {
  const at = value ? parseStoredUtc(value) : null;
  if (!at) return '';
  return at.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "18 Oct 2026" for an event, on the event's own calendar. */
function eventDateLabel(wallClock) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(wallClock || '');
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function distanceLabel(route) {
  const total = Number(route?.total_distance);
  if (!route || !Number.isFinite(total) || total <= 0) return '';
  const unit = text(route.unit?.short_form) || 'km';
  return `${Number(total.toFixed(2))} ${unit}`;
}

const initialsOf = (name) => name.split(/\s+/).filter(Boolean).map((p) => [...p][0]).join('').slice(0, 2).toUpperCase();

/**
 * One view record → what a card needs: who, the event, the activity, the
 * facts row, the certificate values (certificateValues: real data only, no
 * sample text), the template link and the course for the map.
 */
export function toCertificateCard(record) {
  const info = asObject(record?.pass?.user_info);
  const event = record?.event || {};
  const activity = record?.activity || {};
  const route = activity.route || null;
  const venue = activity.venue || null;
  const choice = record?.choice || {};
  const firstName = text(info.first_name);
  const lastName = text(info.last_name);
  const name = [firstName, lastName].filter(Boolean).join(' ');
  const eventWallClock = utcToWallClock(event.start_datetime, event.timezone) || '';
  const finishTime = text(record?.finish_time);
  const position = record?.position === null || record?.position === undefined ? '' : text(record.position);
  const distance = distanceLabel(route);
  const course = {
    routeLine: route?.route_polyline || null,
    venue: venue?.venue_point || null,
    venueName: text(venue?.venue_name),
  };

  return {
    id: String(record?.id ?? ''),
    certificateNo: text(record?.certificate_no),
    name,
    firstName,
    initials: initialsOf(name || 'A'),
    issued: issuedLabel(record?.created_at),
    issuedAt: parseStoredUtc(record?.created_at || '')?.getTime() || 0,
    organizer: text(choice.organization_name),
    event: {
      id: String(event.id ?? record?.event_id ?? ''),
      title: text(event.title),
      date: eventDateLabel(eventWallClock),
      startsAt: parseStoredUtc(event.start_datetime || '')?.getTime() || 0,
      city: text(event.location).split(',')[0].trim(),
    },
    activity: {
      name: text(activity.name),
      icon: sportIcon(activity.activity_type?.code),
    },
    finishTime,
    position,
    facts: [
      distance && { label: 'Distance', value: distance },
      finishTime && { label: 'Finish time', value: finishTime },
      position && { label: 'Position', value: `#${position}` },
    ].filter(Boolean),
    templateUrl: text(choice.template?.template_url),
    templateName: text(choice.template?.name) || 'Certificate',
    course,
    values: certificateValues({
      firstName,
      lastName,
      eventTitle: text(event.title),
      activityName: text(activity.name),
      city: text(event.location),
      organizationName: text(choice.organization_name),
      logoUrl: text(choice.organization_logo_url),
      certificateNo: text(record?.certificate_no),
      finishTime,
      position,
      eventDate: eventWallClock,
      routeLine: course.routeLine,
      venueName: course.venueName,
    }),
  };
}

/** Cards grouped by event, newest event first; within one, newest issued first. */
export function groupByEvent(cards) {
  const groups = new Map();
  (cards || []).forEach((card) => {
    const key = card.event.id;
    if (!groups.has(key)) groups.set(key, { ...card.event, certificates: [] });
    groups.get(key).certificates.push(card);
  });
  return [...groups.values()]
    .map((g) => ({ ...g, certificates: g.certificates.slice().sort((a, b) => b.issuedAt - a.issuedAt) }))
    .sort((a, b) => b.startsAt - a.startsAt);
}

/** Search over event, activity, the printed name and the certificate number. */
export function matchesSearch(card, query) {
  const q = text(query).toLowerCase();
  if (!q) return true;
  return [card.event.title, card.activity.name, card.name, card.certificateNo]
    .some((v) => String(v || '').toLowerCase().includes(q));
}

/**
 * The text that goes with a shared certificate: who did what, where, in what
 * time, and the invitation. `link` is the event's public page.
 */
export function shareMessage(card) {
  const who = card.firstName || card.name || 'I';
  const time = card.finishTime ? ` in ${card.finishTime}` : '';
  const where = card.event.title ? ` at ${card.event.title}` : '';
  return `${who} completed the ${card.activity.name || 'event'}${where}${time}. `
    + 'Join the AthloHive community so friends can do activities together.';
}

/** The public event page, the link a shared certificate carries. */
export function eventLink(card, origin = (typeof window !== 'undefined' ? window.location.origin : '')) {
  return card.event.id ? `${origin}/p/event/${encodeURIComponent(card.event.id)}` : `${origin}/`;
}

/**
 * Every certificate this person holds, as cards. Throws the public view's
 * error (err.kind, e.g. 'auth') when the list could not be read; a later page
 * that fails keeps what came before.
 */
export async function fetchMyCertificates() {
  const first = await fetchPublicTableList(CERTIFICATE_VIEW_SLUG, { page: 1, page_size: PAGE_SIZE });
  const all = Array.isArray(first?.records) ? first.records.slice() : [];
  const total = Number(first?.pagination?.total);
  for (let page = 2; page <= MAX_PAGES; page += 1) {
    if (!Number.isFinite(total) || all.length >= total) break;
    let next;
    try {
      next = await fetchPublicTableList(CERTIFICATE_VIEW_SLUG, { page, page_size: PAGE_SIZE });
    } catch (_) {
      break;
    }
    if (!Array.isArray(next?.records) || next.records.length === 0) break;
    all.push(...next.records);
  }
  return all.map(toCertificateCard);
}
